import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams } from 'react-router-dom';
import WaitlistForm from '../../components/marketing/WaitlistForm';
import { clearWaitlistRef, confirmWaitlistPayment } from '../../services/waitlistService';
import { WAITLIST_THANK_YOU } from '../content/waitlist';
import { isProductionHost } from '../layout/hosts';
import MarketingLayout from '../layout/MarketingLayout';

type Status = 'confirming' | 'confirmed' | 'failed';

/**
 * Where Stripe sends the visitor after the deposit. `session_id` is confirmed
 * against Stripe server-side before anything is shown as reserved; the
 * webhook does the same independently, so a visitor who never lands here is
 * still marked paid. `ref` + `dummy=1` is the no-Stripe path on
 * non-production projects. Once confirmed, the optional step-2 questions are
 * offered here instead of before payment.
 */
export default function WaitlistThankYouPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const dummy = params.get('dummy') === '1';
  const refParam = params.get('ref');
  const [status, setStatus] = useState<Status>(sessionId ? 'confirming' : refParam ? 'confirmed' : 'failed');
  const [entryRef, setEntryRef] = useState<string | null>(refParam);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    confirmWaitlistPayment(sessionId).then((result) => {
      if (cancelled) return;
      if (result.success) {
        setEntryRef(result.ref ?? null);
        setStatus('confirmed');
        clearWaitlistRef();
      } else {
        setStatus('failed');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (status === 'confirmed' && !sessionId) clearWaitlistRef();
  }, [status, sessionId]);

  return (
    <MarketingLayout activePage="home">
      <Helmet>
        <title>Your place is reserved · Melio</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <section className="mk-wl" aria-live="polite">
        <div className="mk-container mk-wl__grid">
          <div>
            <p className="t-details t-muted">The list</p>
            {status === 'confirming' && <h1 className="mk-wl__title t-display">{WAITLIST_THANK_YOU.confirming}</h1>}
            {status === 'confirmed' && (
              <>
                <h1 className="mk-wl__title t-display">{WAITLIST_THANK_YOU.title}</h1>
                <p className="mk-wl__body t-body">{WAITLIST_THANK_YOU.body}</p>
                {dummy && !isProductionHost() && <p className="t-details t-muted" style={{ marginTop: '1rem' }}>{WAITLIST_THANK_YOU.testMode}</p>}
              </>
            )}
            {status === 'failed' && (
              <>
                <h1 className="mk-wl__title t-display">{WAITLIST_THANK_YOU.failedTitle}</h1>
                <p className="mk-wl__body t-body">{WAITLIST_THANK_YOU.failedBody}</p>
                <p className="mk-wl__aside t-body">
                  <Link to="/waitlist">Back to the waitlist</Link>
                </p>
              </>
            )}
          </div>
          {status === 'confirmed' && entryRef && (
            <div className="mk-wl__form">
              <p className="t-details t-muted" style={{ marginBottom: '1rem' }}>
                While you are here
              </p>
              <WaitlistForm sourceSurface="waitlist_page" variant="inline" initialPhase="details" entryRef={entryRef} />
            </div>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
